import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiSmartphone, FiBell, FiShield, FiVideo } = FiIcons;

const CandidateExperience = () => {
  const features = [
    {
      icon: FiMessageCircle,
      title: 'WhatsApp/Email Invite',
      description: 'Candidates receive friendly invitations through their preferred channel'
    },
    {
      icon: FiSmartphone,
      title: 'Mobile-First Interview',
      description: 'Quick 6-minute video interviews optimized for mobile devices'
    },
    {
      icon: FiBell,
      title: 'Real-Time Updates',
      description: 'Instant status notifications keep candidates informed throughout'
    },
    {
      icon: FiShield,
      title: 'Proxy Detection',
      description: 'AI-powered fairness ensuring authentic candidate responses'
    }
  ];

  return (
    <section id="candidate-experience" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-6">
            Exceptional Candidate Experience
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Keep candidates engaged with transparent, mobile-friendly interactions
          </p>
        </motion.div>

        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-8 lg:p-12 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Testimonial Card */}
        <motion.div
          className="bg-white border border-slate-200 rounded-2xl p-8 shadow-elegant max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">SK</span>
              </div>
            </div>
            <div className="flex-1">
              <blockquote className="text-lg text-slate-700 italic mb-4">
                "The video interview took 6 minutes — I got status updates the same day.
                Much better than the usual black hole experience!"
              </blockquote>
              <div className="flex items-center space-x-4">
                <div>
                  <div className="font-semibold text-slate-900">Sarah Kim</div>
                  <div className="text-sm text-slate-500">Software Engineer</div>
                </div>
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>⭐</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Mobile Mockup */}
        <motion.div
          className="mt-16 flex justify-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="relative">
            {/* Phone Frame */}
            <div className="bg-slate-900 rounded-3xl p-2 shadow-2xl">
              <div className="bg-white rounded-2xl overflow-hidden w-64 h-96">
                {/* Status Bar */}
                <div className="bg-slate-100 px-4 py-2 flex justify-between items-center text-xs">
                  <span className="font-medium">9:41</span>
                  <div className="flex space-x-1">
                    <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                    <div className="w-4 h-2 bg-slate-300 rounded-sm"></div>
                    <div className="w-4 h-2 bg-slate-300 rounded-sm"></div>
                  </div>
                </div>

                {/* Interview Screen */}
                <div className="p-4 h-full bg-gradient-to-br from-indigo-50 to-purple-50">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-indigo-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                      <SafeIcon icon={FiVideo} className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900">Video Interview</h3>
                    <p className="text-sm text-slate-600">Senior Developer Role</p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-3">
                      <div className="text-sm font-medium text-slate-900 mb-1">Question 1 of 3</div>
                      <div className="text-xs text-slate-600">Tell us about your experience with React</div>
                    </div>

                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="w-8 h-8 bg-red-500 rounded-full mx-auto mb-2"></div>
                      <div className="text-xs text-slate-600">Recording... 02:34</div>
                    </div>

                    <button className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium">
                      Next Question
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CandidateExperience;